import { makeStyles, mergeClasses } from '@fluentui/react-components';

import { formatHM } from '../common/format';
import { DonutChart } from '../components';
import { palette, tokens } from '../theme';

export type DayCellHeaderProps = {
    day: number;
    isToday: boolean;
    isFuture: boolean;
    showProgress: boolean;
    hasAllocation: boolean;
    totalHours: number;
    capacity: number;
    savedFrac: number;
    pendingFrac: number;
};

export const DayCellHeader: React.FC<DayCellHeaderProps> = ({
    day,
    isToday,
    isFuture,
    showProgress,
    hasAllocation,
    totalHours,
    capacity,
    savedFrac,
    pendingFrac,
}) => {
    const classes = useStyles();

    return (
        <div className={classes.headerRow}>
            <span
                className={mergeClasses(classes.dayNumber, isToday && classes.todayPill, !isToday && isFuture && classes.dayNumberFuture)}
            >
                {day}
            </span>
            {showProgress &&
                (hasAllocation ? (
                    <span className={classes.hoursGroup}>
                        <span className={classes.hours}>
                            <span className={classes.hoursLogged}>{formatHM(totalHours)}</span>
                            <span className={classes.hoursSep} aria-hidden>
                                /
                            </span>
                            <span className={classes.hoursCapacity}>{formatHM(capacity)}</span>
                        </span>
                        <DonutChart
                            size={12}
                            strokeWidth={2}
                            trackColor={palette.progressTrackBg}
                            segments={[
                                { fraction: savedFrac, color: palette.goodAccent },
                                { fraction: pendingFrac, color: palette.pendingAccent },
                            ]}
                        />
                    </span>
                ) : (
                    <span className={classes.hoursEmpty}>{formatHM(totalHours)}</span>
                ))}
        </div>
    );
};

const useStyles = makeStyles({
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '28px',
        flexShrink: 0,
    },
    dayNumber: {
        fontSize: '18px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    dayNumberFuture: {
        color: palette.dimText,
        fontWeight: tokens.fontWeightRegular,
    },
    todayPill: {
        backgroundColor: palette.todayAccent,
        color: palette.primaryText,
        fontSize: '17px',
        borderRadius: '14px',
        minWidth: '28px',
        height: '28px',
        padding: '0 8px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    hoursGroup: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '3px 8px',
        borderRadius: palette.pillRadius,
        backgroundColor: palette.panelMuted,
    },
    hours: {
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '3px',
        fontSize: '11.5px',
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
    },
    hoursLogged: {
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    hoursSep: {
        fontWeight: 300,
        color: palette.outlineGray,
    },
    hoursCapacity: {
        fontWeight: tokens.fontWeightRegular,
        color: palette.dimText,
    },
    hoursEmpty: {
        fontSize: '11.5px',
        fontWeight: tokens.fontWeightRegular,
        color: tokens.colorNeutralForeground3,
        fontVariantNumeric: 'tabular-nums',
    },
});
